import type { Agent, Team } from "@/types/team";
import { type ErrorType, type PageRes, apiRequest } from "../api";

interface TeamQueryParams {
  page?: number;
  limit?: number;
  search?: {
    s_name?: string;
    s_and_name?: string;
    s_or_name?: string;
    // Outros critérios de busca conforme documentação
  };
}

export const teamService = {
  async getAll(
    query: TeamQueryParams,
    page: number,
    limit: number
  ): Promise<[PageRes<Team> | null, ErrorType | null]> {
    const result = await apiRequest<PageRes<Team>>({
      loaderKey: "getAllTeams",
      method: "get",
      url: "/app/teams",
      params: { ...query, page, limit },
    });
    return result;
  },

  async getById(id: string): Promise<[Team | null, ErrorType | null]> {
    return apiRequest<Team>({
      loaderKey: "getTeamById",
      method: "get",
      url: `/app/teams/${id}`,
    });
  },

  async create(data: Partial<Team>): Promise<[Team | null, ErrorType | null]> {
    return apiRequest<Team>({
      loaderKey: "createTeam",
      method: "post",
      url: "/app/teams",
      data,
    });
  },

  async update(
    id: string,
    data: Partial<Team>
  ): Promise<[Team | null, ErrorType | null]> {
    return apiRequest<Team>({
      loaderKey: "updateTeam",
      method: "put",
      url: `/app/teams/${id}`,
      data,
    });
  },

  async delete(
    id: string
  ): Promise<[Record<string, never> | null, ErrorType | null]> {
    return apiRequest<Record<string, never>>({
      loaderKey: "deleteTeam",
      method: "delete",
      url: `/app/teams/${id}`,
    });
  },

  async addAgent(
    teamId: string,
    data: string[]
  ): Promise<[Agent | null, ErrorType | null]> {
    return apiRequest<Agent>({
      loaderKey: "addAgent",
      method: "post",
      url: `/app/teams/${teamId}/members`,
      data: { userIds: data },
    });
  },

  async updateAgent(
    teamId: string,
    agentId: string,
    data: Partial<Agent>
  ): Promise<[Agent | null, ErrorType | null]> {
    return apiRequest<Agent>({
      loaderKey: "updateAgent",
      method: "put",
      url: `/app/teams/${teamId}/members/${agentId}`,
      data,
    });
  },

  async removeAgent(
    teamId: string,
    agentId: string
  ): Promise<[Record<string, never> | null, ErrorType | null]> {
    return apiRequest<Record<string, never>>({
      loaderKey: "removeAgent",
      method: "delete",
      url: `/app/teams/${teamId}/members/${agentId}`,
    });
  },

  async getAgents(
    teamId: string,
    params: TeamQueryParams = {}
  ): Promise<[PageRes<Agent> | null, ErrorType | null]> {
    return apiRequest<PageRes<Agent>>({
      loaderKey: "getTeamAgents",
      method: "get",
      url: `/app/teams/${teamId}/members`,
      params,
    });
  },
};
